import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { StorageService } from "src/services/storage.service";
import { AccountService } from "src/services/account.service";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import * as Papa from "papaparse";

@Component({
  selector: "app-upload-employees-dialog",
  templateUrl: "./upload-employees-dialog.component.html",
  styleUrls: ["./upload-employees-dialog.component.css"],
})
export class UploadEmployeesDialogComponent {
  private _snackBar = inject(MatSnackBar);
  private _storageService = inject(StorageService);
  private _accountService = inject(AccountService);
  dialogRef = inject(MatDialogRef<UploadEmployeesDialogComponent>);
  @ViewChild("csvFileInput") csvFileInput: ElementRef;
  @ViewChild("xlsFileInput") xlsFileInput: ElementRef;

  newCustomersFile: any;
  newCustomersEle: string = "";
  isCreatingBatch = false;
  processing = false;
  agencyUserId: string = "";
  xlsFile: any;
  xlsFileName: string = "";

  ngOnInit(): void {
    this._accountService.getAccount().subscribe((account) => {
      this.agencyUserId = account.id;
    });
  }

  onnewCustomers(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.newCustomersEle = file.name;
    this.newCustomersFile = file;
  }

  uploadCSV() {
    if (this.newCustomersFile) {
      this.processAndUploadCSV(this.newCustomersFile);
    }
  }

  onXlsFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.xlsFileName = file.name;
    this.xlsFile = file;
  }

  uploadXls() {
    if (this.xlsFile) {
      this.uploadXlsToCloud(this.xlsFile);
    }
  }

  private processAndUploadCSV(file: File) {
    this.processing = true;

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => {
        const headerMap: Record<string, string> = {
          firstname: "firstName",
          lastname: "lastName",
          emailaddress: "email",
          email: "email",
          phonenumber: "phoneNumber",
          phone: "phoneNumber",
          dateofbirth: "dateOfBirth",
          dob: "dateOfBirth",
          gender: "gender",
          employeeid: "employeeId",
        };
        const key = header.trim().toLowerCase().replace(/\s+/g, "");
        return (
          headerMap[key] ??
          header.trim().charAt(0).toLowerCase() + header.trim().slice(1)
        );
      },
      complete: (result: { data: CareplusNewCustomer[] }) => {
        const requiredFields: (keyof CareplusNewCustomer)[] = [
          "firstName",
          "lastName",
          "phoneNumber",
          "email",
          "dateOfBirth",
          "gender",
        ];

        for (let i = 0; i < result.data.length; i++) {
          const row = result.data[i];
          const missingFields = requiredFields.filter(
            (field) =>
              row[field] === null ||
              row[field] === undefined ||
              row[field]?.toString().trim() === "",
          );

          if (missingFields.length > 0) {
            this.processing = false;
            this._snackBar.open(
              `Row ${i + 1} is missing required fields: ${missingFields.join(", ")}. Please fix the CSV and try again.`,
              "CLOSE",
              { duration: 8000 },
            );
            return;
          }
        }

        const processedData = result.data.map((row) => ({
          ...row,
          dateOfBirth: this.parseDateString(row.dateOfBirth as any),
        }));

        const processedCsv = Papa.unparse(processedData);
        const processedBlob = new Blob([processedCsv], { type: "text/csv" });
        const processedFile = new File([processedBlob], file.name, {
          type: "text/csv",
        });
        this.uploadToCloud(processedFile);
      },
      error: () => {
        this.processing = false;
        this._snackBar.open("Failed to process CSV file.", "CLOSE", {
          duration: 5000,
        });
      },
    });
  }

  private uploadToCloud(file: File) {
    this.isCreatingBatch = true;

    const d = new Date().getTime().toString();
    const fileName = `${d}-agencyUserId:${this.agencyUserId}`;

    this._storageService
      .getCareplusUploadOnlyToken("renew-carepluscustomer-csv")
      .subscribe((token) => {
        this._storageService
          .uploadFileToAzure(
            `/renew-carepluscustomer-csv/${fileName}.csv${token.sasToken}`,
            file,
            file.type,
          )
          .subscribe({
            next: () => {
              this.isCreatingBatch = false;
              this.processing = false;
              this._snackBar.open("The file has been uploaded.", "CLOSE", {
                horizontalPosition: "right",
                duration: 4000,
              });
              this.dialogRef.close(true);
              location.reload();
            },
            error: () => {
              this.isCreatingBatch = false;
              this.processing = false;
              this._snackBar.open("Failed to upload data to cloud.", "CLOSE", {
                duration: 5000,
              });
            },
          });
      });
  }

  downloadSampleCSV() {
    const link = document.createElement("a");
    link.href = "assets/files/careplus_template.csv";
    link.download = "careplus_template.csv";
    link.click();
  }

  downloadSampleXLS() {
    const link = document.createElement("a");
    link.href = "assets/files/xls_template.xlsx";
    link.download = "xls_template.xlsx";
    link.click();
  }

  private uploadXlsToCloud(file: File) {
    this.processing = true;

    const d = new Date().getTime().toString();
    const fileName = `${d}-agencyUserId:${this.agencyUserId}`;

    this._storageService
      .getCareplusUploadOnlyToken("renew-carepluscustomerwithmember-xls")
      .subscribe((token) => {
        this._storageService
          .uploadFileToAzure(
            `/renew-carepluscustomerwithmember-xls/${fileName}.xlsx${token.sasToken}`,
            file,
            file.type,
          )
          .subscribe({
            next: () => {
              this.processing = false;
              this.xlsFileName = "";
              this.xlsFile = undefined;
              if (this.xlsFileInput)
                this.xlsFileInput.nativeElement.value = null;

              this._snackBar.open("The XLS file has been uploaded.", "CLOSE", {
                horizontalPosition: "right",
                duration: 4000,
              });
              this.dialogRef.close(true);
              location.reload();
            },
            error: () => {
              this.processing = false;
              this._snackBar.open("Failed to upload XLS to cloud.", "CLOSE", {
                duration: 5000,
              });
            },
          });
      });
  }

  private parseDateString(dateStr: string | undefined): string {
    if (!dateStr) return "";
    const dateString = dateStr.toString().trim();
    if (!dateString) return "";
    const parts = dateString.split(/[-\/]/);
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (isNaN(day) || isNaN(month) || isNaN(year)) return dateString;
      return `${day.toString().padStart(2, "0")}-${month.toString().padStart(2, "0")}-${year}`;
    }
    return dateString;
  }

  close() {
    this.dialogRef.close(false);
  }
}
